/**
 * Skeleton loading screen for the moderation and editorial dashboards.
 *
 * Both dashboards share one frame: the nav drawer down the left, the
 * breadcrumb header across the top, and tab content that opens with a
 * bordered title card of a heading and filter pills, then a bordered panel
 * of rows. The skeleton draws that frame, so it fits every tab of either
 * dashboard without knowing which one is loading.
 */

import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, Pad, PadBox, VertBox } from 'component/basics';
import { spacings } from 'component/constants';
import { BaseSkeleton } from 'component/progress/base-skeleton';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { SkeletonNavBar } from 'component/progress/skeleton-nav-bar';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { ScrollView, View } from 'react-native';

const TAB_LABEL_WIDTHS = [80, 64, 104];
const FILTER_PILL_WIDTHS = [150, 170, 140, 120];
const ROW_COUNT = 4;

// Mirrors `DashboardNavDrawer`: the dashboard title above one pill per tab.
function DashboardNavDrawerSkeleton() {
    const s = useStyles();
    return (
        <View style={s.drawer}>
            <PadBox horiz={40} vert={40}>
                <TextContainerSkeleton borderRadius={2} fade height={10} width={120} />
                <Pad size={40} />
                <VertBox gap={16}>
                    {TAB_LABEL_WIDTHS.map((width, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <View key={i} style={s.tab}>
                            <BaseSkeleton borderRadius={4} height={20} marginRight={12} width={20} />
                            <TextContainerSkeleton borderRadius={2} fade height={8} width={width} />
                        </View>
                    ))}
                </VertBox>
            </PadBox>
        </View>
    );
}

// Mirrors the dashboard's `NavigationHeader`: a breadcrumb on the left and the
// profile button on the right.
function DashboardHeaderSkeleton() {
    return (
        <SkeletonNavBar paddingHorizontal={20}>
            <TextContainerSkeleton borderRadius={2} fade height={8} width={60} />
            <HorizBox center gap={8}>
                <BaseSkeleton borderRadius={16} height={32} width={32} />
                <TextContainerSkeleton borderRadius={2} fade height={8} width={100} />
            </HorizBox>
        </SkeletonNavBar>
    );
}

// Mirrors `DashboardTitleCard`: the tab's heading above its filter pills.
function DashboardTitleCardSkeleton() {
    const s = useStyles();
    return (
        <View style={s.card}>
            <PadBox horiz={20} vert={32}>
                <TextContainerSkeleton borderRadius={4} fade height={14} width={200} />
                <Pad size={32} />
                <HorizBox gap={12} wrap>
                    {FILTER_PILL_WIDTHS.map((width, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <BaseSkeleton borderRadius={16} height={32} key={i} width={width} />
                    ))}
                </HorizBox>
            </PadBox>
        </View>
    );
}

// Stands in for the tab's list: moderation queue cards or table rows.
function DashboardRowsSkeleton() {
    const s = useStyles();
    return (
        <View style={s.card}>
            <PadBox horiz={20} vert={12}>
                {Array.from({ length: ROW_COUNT }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <View key={i} style={[s.row, i < ROW_COUNT - 1 && s.rowDivider]}>
                        <BaseSkeleton borderRadius={16} height={32} marginRight={12} width={32} />
                        <View style={s.rowText}>
                            <TextContainerSkeleton borderRadius={2} fade height={6} width={240} />
                            <Pad size={8} />
                            <View style={s.rowBody}>
                                <TextContainerSkeleton borderRadius={2} fade height={6} />
                            </View>
                        </View>
                    </View>
                ))}
            </PadBox>
        </View>
    );
}

export function SkeletonDashboardScreen() {
    const s = useStyles();
    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <DashboardNavDrawerSkeleton />
            <View style={s.main}>
                <DashboardHeaderSkeleton />
                <ScrollView style={s.scroller}>
                    <PadBox horiz={spacings.xl3} vert={spacings.xl3}>
                        <DashboardTitleCardSkeleton />
                        <Pad size={20} />
                        <DashboardRowsSkeleton />
                    </PadBox>
                </ScrollView>
            </View>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, flexDirection: 'row', backgroundColor: tokens.colors.colorSurfacePrimary },
    drawer: { width: 300, borderRightWidth: 1, borderRightColor: tokens.colors.colorBorderPrimary },
    tab: { flexDirection: 'row', alignItems: 'center', height: 44, paddingHorizontal: 12 },
    main: { flex: 1 },
    scroller: { flex: 1 },
    card: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: tokens.colors.colorBorderPrimary,
    },
    row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 20 },
    rowDivider: { borderBottomWidth: 1, borderBottomColor: tokens.colors.colorBorderPrimary },
    rowText: { flex: 1 },
    rowBody: { maxWidth: 480 },
}));
